import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Close,
} from '@mui/icons-material';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent } from '@ionic/react';
import { 
  addExternalProcessorAPI, 
  getExternalProcessorsAPI, 
  updateExternalProcessorAPI, 
  deleteExternalProcessorAPI 
} from '../../../api/orders/orders';
import { MenuButton } from '../../../components/Menu';

interface ExternalProcessor {
  id: number;
  name: string;
  processor_id: string;
  business_id: string;
  dateCreated: string;
  type: 'USD' | 'CHINESE'; // Added processor type
}

const SetupExternalProcessorsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [externalProcessors, setExternalProcessors] = useState<ExternalProcessor[]>([]);
  const [processors, setProcessors] = useState<ExternalProcessor[]>([]);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProcessor, setSelectedProcessor] = useState<ExternalProcessor | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    processor_id: '',
    business_id: 'business_001', // Default business ID
    type: 'USD' as 'USD' | 'CHINESE', // Default processor type
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch processors on component mount
  useEffect(() => {
    fetchExternalProcessors();
  }, []);

  // Filter processors based on search term
  const filteredProcessors = processors.filter(processor =>
    processor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    processor.processor_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    processor.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fetchExternalProcessors = async () => {
    setLoading(true);
    try {
      const response = await getExternalProcessorsAPI();
      setExternalProcessors(response.data);
      setProcessors(response.data);
      console.log("Fetched external processors:", response.data);
    } catch (error) {
      console.error("Error fetching external processors:", error);
      showSnackbar('Failed to load external processors', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProcessor = async () => {
    if (!formData.name || !formData.processor_id || !formData.type) {
      showSnackbar('Name, Processor ID and Type are required', 'error');
      return;
    }

    setActionLoading(true);
    try {
      // Call the API to add new processor
      const response = await addExternalProcessorAPI(formData);
      
      // Refresh the processor list
      await fetchExternalProcessors();
      
      setAddDialogOpen(false);
      resetForm();
      showSnackbar('Processor added successfully', 'success');
    } catch (error) {
      console.error("Error adding processor:", error);
      showSnackbar('Failed to add processor', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditProcessor = async () => {
    if (!selectedProcessor || !formData.name || !formData.type) {
      showSnackbar('Name and Type are required', 'error');
      return;
    }

    setActionLoading(true);
    try {
      // Prepare data for update
      const updateData = {
        id: selectedProcessor.id,
        name: formData.name,
        processor_id: selectedProcessor.processor_id, // Keep original processor_id
        business_id: selectedProcessor.business_id, // Keep original business_id
        type: formData.type, // Include processor type
      };

      // Call the API to update processor
      const response = await updateExternalProcessorAPI(updateData);
      
      // Refresh the processor list
      await fetchExternalProcessors();
      
      setEditDialogOpen(false);
      resetForm();
      showSnackbar('Processor updated successfully', 'success');
    } catch (error) {
      console.error("Error updating processor:", error);
      showSnackbar('Failed to update processor', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteProcessor = async () => {
    if (!selectedProcessor) return;

    setActionLoading(true);
    try {
      // Call the API to delete processor
      const response = await deleteExternalProcessorAPI(selectedProcessor.processor_id);
      
      // Refresh the processor list
      await fetchExternalProcessors();
      
      setDeleteDialogOpen(false);
      showSnackbar('Processor deleted successfully', 'success');
    } catch (error) {
      console.error("Error deleting processor:", error);
      showSnackbar('Failed to delete processor', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openAddDialog = () => {
    resetForm();
    setAddDialogOpen(true);
  };

  const openEditDialog = (processor: ExternalProcessor) => {
    setSelectedProcessor(processor);
    setFormData({
      name: processor.name,
      processor_id: processor.processor_id,
      business_id: processor.business_id,
      type: processor.type, // Set the processor type
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (processor: ExternalProcessor) => {
    setSelectedProcessor(processor);
    setFormData({
      name: processor.name,
      processor_id: processor.processor_id,
      business_id: processor.business_id,
      type: processor.type, // Include type in delete dialog for display
    });
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      processor_id: '',
      business_id: 'business_001',
      type: 'USD', // Reset to default type
    });
    setSelectedProcessor(null);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Generate processor ID from name
  const generateProcessorId = (name: string) => {
    return name.toUpperCase().replace(/\s+/g, '_');
  };

  // Update processor ID when name changes in add form
  const handleNameChange = (value: string) => {
    setFormData({
      ...formData,
      name: value,
      processor_id: generateProcessorId(value)
    });
  };

  // Get chip color based on processor type
  const getTypeChipColor = (type: 'USD' | 'CHINESE') => {
    return type === 'USD' ? 'primary' : 'secondary';
  };

  return (
    <IonPage className='pushDown'>
      <IonHeader>
        <IonToolbar>  <MenuButton/>
          <IonTitle>Setup External Processors</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              External Processors Setup
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage external payment processors for your business
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Processors
                  </Typography>
                  <Typography variant="h4">
                    {processors.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    USD Processors
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {processors.filter(p => p.type === 'USD').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Chinese Processors
                  </Typography>
                  <Typography variant="h4" color="secondary.main">
                    {processors.filter(p => p.type === 'CHINESE').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Action Bar */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search processors by name, ID, or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={openAddDialog}
                >
                  Add New Processor
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Processors Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Processor ID</TableCell>
                    <TableCell>Processor Type</TableCell>
                    <TableCell>Date Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Loading processors...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredProcessors?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm ? 'No processors found matching your search' : 'No processors found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProcessors?.map((processor) => (
                      <TableRow key={processor?.id} hover>
                        <TableCell>{processor?.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {processor?.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={processor?.processor_id} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={processor?.type} 
                            size="small" 
                            color={getTypeChipColor(processor?.type)}
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {processor?.dateCreated || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="primary"
                            onClick={() => openEditDialog(processor)}
                            size="small"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => openDeleteDialog(processor)}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Add Processor Dialog */}
          <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Add New Processor
              <IconButton
                sx={{ position: 'absolute', right: 8, top: 8 }}
                onClick={() => setAddDialogOpen(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Processor Name"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="e.g., Paystack, Stripe"
                    helperText="Processor ID will be auto-generated from the name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Processor ID"
                    value={formData.processor_id}
                    disabled
                    helperText="Auto-generated identifier"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Processor Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Processor Type"
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as 'USD' | 'CHINESE' })}
                    >
                      <MenuItem value="USD">USD Processor</MenuItem>
                      <MenuItem value="CHINESE">Chinese Processor</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleAddProcessor}
                disabled={actionLoading || !formData.name || !formData.type}
                startIcon={actionLoading ? <CircularProgress size={20} /> : null}
              >
                {actionLoading ? 'Adding...' : 'Add Processor'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Processor Dialog */}
          <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Edit Processor
              <IconButton
                sx={{ position: 'absolute', right: 8, top: 8 }}
                onClick={() => setEditDialogOpen(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Processor Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Processor ID"
                    disabled
                    value={formData.processor_id}
                    helperText="Processor ID cannot be changed"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Processor Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Processor Type"
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as 'USD' | 'CHINESE' })}
                    >
                      <MenuItem value="USD">USD Processor</MenuItem>
                      <MenuItem value="CHINESE">Chinese Processor</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleEditProcessor}
                disabled={actionLoading || !formData.name || !formData.type}
                startIcon={actionLoading ? <CircularProgress size={20} /> : null}
              >
                {actionLoading ? 'Updating...' : 'Update Processor'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Delete Processor
              <IconButton
                sx={{ position: 'absolute', right: 8, top: 8 }}
                onClick={() => setDeleteDialogOpen(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Are you sure you want to delete this processor? This action cannot be undone.
              </Alert>
              <Typography variant="body2">
                <strong>Name:</strong> {formData.name}
              </Typography>
              <Typography variant="body2">
                <strong>Processor ID:</strong> {formData.processor_id}
              </Typography>
              <Typography variant="body2">
                <strong>Processor Type:</strong> {formData.type}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteProcessor}
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={20} /> : null}
              >
                {actionLoading ? 'Deleting...' : 'Delete Processor'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default SetupExternalProcessorsPage;